import { useState } from 'react'
import { Modal } from '@/components/common/Modal'
import { OrderTicketModal } from '@/components/orders/OrderTicketModal'
import { formatMoney, formatPct } from '@/lib/format'
import { TAX_STATUS_LABEL } from '@/lib/rebalanceLabels'
import type { ProfitTakingPosition } from '@/types/profitTaking'
import type { TaxStatus } from '@/types/rebalance'

interface Props {
  open: boolean
  onClose: () => void
  position: ProfitTakingPosition
}

export function ProfitRecommendationModal({ open, onClose, position }: Props) {
  const [ticketOpen, setTicketOpen] = useState(false)
  const rec = position.recommendation

  if (!rec)
    return (
      <Modal open={open} onClose={onClose} title="Empfehlung">
        {null}
      </Modal>
    )

  const hasQuantity = Number(rec.sell_quantity) > 0

  return (
    <Modal open={open} onClose={onClose} title="Empfehlung" widthClass="max-w-2xl">
      <div className="flex flex-col gap-4 font-mono text-sm">
        {/* 2. Situation */}
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[11px] text-muted">
          <div className="flex justify-between">
            <dt>Einstand</dt>
            <dd className="text-ink">{formatMoney(position.avg_cost, position.currency)}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Aktuell</dt>
            <dd className="text-ink">{formatMoney(position.current_price, position.currency)}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Gewinn</dt>
            <dd className="text-ink">{formatPct(position.gain_pct)}</dd>
          </div>
          <div className="flex justify-between">
            <dt>steuerfrei</dt>
            <dd className="text-ink">{position.tax_free_quantity}</dd>
          </div>
        </dl>

        {/* 3. Steuer-Check */}
        <p className="text-xs text-muted">
          Steuer: {TAX_STATUS_LABEL[rec.tax_status as TaxStatus]}
          {rec.kest_eur && Number(rec.kest_eur) > 0 && (
            <span> · {formatMoney(rec.kest_eur, position.currency)} KESt</span>
          )}
        </p>

        {/* 4. Konkrete Empfehlung */}
        {hasQuantity ? (
          <p className="text-base text-ink">
            <strong>{rec.sell_quantity} Stück verkaufen</strong> (≈{' '}
            {formatMoney(rec.sell_value_eur, position.currency)})
          </p>
        ) : (
          <p className="text-base text-bernstein">{rec.holding_note}</p>
        )}

        {/* 5. Pro / Contra */}
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div>
            <p className="tracked-caps mb-1 text-[10px] text-muted">Dafür</p>
            <ul className="flex flex-col gap-1 text-ink">
              {rec.pro.map((p) => (
                <li key={p}>+ {p}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="tracked-caps mb-1 text-[10px] text-muted">Dagegen</p>
            <ul className="flex flex-col gap-1 text-ink">
              {rec.contra.map((c) => (
                <li key={c}>− {c}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* 6. Reinvest */}
        {rec.reinvest_hint && <p className="text-xs text-muted">{rec.reinvest_hint}</p>}

        {/* 7. Order-Ticket */}
        {hasQuantity && (
          <button
            type="button"
            onClick={() => setTicketOpen(true)}
            className="tracked-caps self-end border border-hairline px-4 py-2 text-[10px] text-bernstein hover:bg-panel"
          >
            Order-Ticket erstellen
          </button>
        )}
      </div>

      <OrderTicketModal
        open={ticketOpen}
        onClose={() => setTicketOpen(false)}
        positionId={position.position_id}
        positionName={position.name}
        direction="sell"
        defaultOrderType="market"
        prefillEurAmount={Number(rec.sell_value_eur)}
      />
    </Modal>
  )
}
